import { Plus } from 'lucide-react';
import { Z } from './lib/z-index.js';
import { NAV_BOTTOM_OFFSET, NAV_FLOATING_HEIGHT } from './floating-bottom-nav.jsx';

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

// Visible gap between the floating button and the top of the bottom-nav pill.
// Button-bottom = nav offset + pill height + gap = nav top edge + 16px.
const FLOATING_GAP = 16;
const FLOATING_BOTTOM = `calc(${NAV_BOTTOM_OFFSET} + ${NAV_FLOATING_HEIGHT + FLOATING_GAP}px)`;

// Horizontal inset of the Tage <main> (px-5 = 20px); cancelled so the sticky band is full-bleed.
const STICKY_BLEED = 20;

/**
 * ActionButton — shared labeled action pill (accent fill + Plus + label).
 *
 * Replaces the three bare "+" FABs with one consistent look.
 *   variant 'floating' — bottom-right, clearly above the bottom-nav (Online, Sortiment)
 *   variant 'sticky'   — full-width pill in a glass band that sticks under the header (Tage)
 *
 * Accent comes in as a CSS-variable string (e.g. 'var(--accent-online)'); the white
 * foreground reuses the old FABs' text-white convention (keeps the no-hardcoded-hex rule).
 *
 * Props: label, icon (default Plus), accent, onClick, variant, ariaLabel
 */
export function ActionButton({
  label,
  icon: Icon = Plus,
  accent = 'var(--accent-online)',
  onClick,
  variant = 'floating',
  ariaLabel,
}) {
  const pill = (extraStyle, fullWidth) => (
    <button
      type="button"
      data-testid="action-button"
      aria-label={ariaLabel || label}
      onClick={onClick}
      className="text-white active:scale-[0.96]"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: fullWidth ? 'center' : 'flex-start',
        gap: 8,
        width: fullWidth ? '100%' : undefined,
        border: 'none',
        cursor: 'pointer',
        fontSize: 15,
        fontWeight: 650,
        letterSpacing: '-0.01em',
        background: accent,
        borderRadius: 9999,
        padding: fullWidth ? 15 : '13px 20px 13px 16px',
        transition: `transform 0.18s ${EASE}, filter 0.18s ease`,
        ...extraStyle,
      }}
    >
      <Icon size={20} strokeWidth={2.6} className="text-white" />
      <span>{label}</span>
    </button>
  );

  if (variant === 'sticky') {
    return (
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          marginLeft: -STICKY_BLEED,
          marginRight: -STICKY_BLEED,
          padding: '6px 16px 14px',
          background: 'linear-gradient(to bottom, var(--sticky-fill) 60%, transparent)',
          backdropFilter: 'blur(18px) saturate(180%)',
          WebkitBackdropFilter: 'blur(18px) saturate(180%)',
        }}
      >
        {pill(
          // Schwarzer Schatten statt farbigem Glow (Design-Regel: keine Akzent-Glows)
          { boxShadow: 'var(--shine-button), var(--shadow-md)' },
          true,
        )}
      </div>
    );
  }

  // floating
  return pill(
    {
      position: 'fixed',
      right: 16,
      bottom: FLOATING_BOTTOM,
      zIndex: Z.fab,
      boxShadow: 'var(--shine-button), var(--shadow-md)',
    },
    false,
  );
}
